<template>
  <div v-if="!hidePostVideoConference" class="d-flex justify-content-center">
    <div ref="post" class="card__post" :class="{ 'card card-box': isShowFullContent }">
      <div class="card-body">
        <slot :post="post" name="alert" />

        <!-- Date -->
        <div
          v-if="showDate || isPrivate"
          class="d-flex mb-2 justify-content-between align-items-center"
        >
          <div v-if="showDate" class="post__date text__publish-time">
            {{ scheduleDateString }}
          </div>

          <span v-if="isPrivate" ref="label__visibility" class="text-dark">
            <font-awesome-icon icon="eye" /> Only visible to you
          </span>

          <BaseEllipsis v-if="editable" ref="ellipsis" :disabled="disabled">
            <b-dropdown-item
              class="dropdown-menu__edit"
              @click.prevent.stop="goToEditPost(post.id)"
            >
              Edit
            </b-dropdown-item>
            <b-dropdown-item
              class="dropdown-menu__delete"
              :disabled="learningUnit.is_published"
              @click.prevent.stop="showModalDelete = true"
            >
              Delete
            </b-dropdown-item>
          </BaseEllipsis>

          <ModalDeletePost
            :ref="`modalDeletePost__${post.id}`"
            :post="post"
            :show.sync="showModalDelete"
            @emitDeletePost="emitDeletePost"
          />
        </div>

        <!-- Badge -->
        <div ref="tag__post" class="d-flex flex-row tag__post">
          <div v-for="(badge, index) in badges" :key="index" class="mr-2">
            <b-badge :variant="badge.variant" :class="badge.class">
              {{ badge.text }}
            </b-badge>
          </div>

          <b-badge
            v-if="post && post.meeting"
            ref="tag__publish"
            class="tag__post"
            :class="tagPublishClasses"
            :variant="tagPublishVariant"
          >
            {{ post.is_published ? 'Published' : 'Unpublished' }}
          </b-badge>
        </div>

        <!-- <div
          v-if="post.meeting && post.meeting.start_time"
          ref="text__meeting-datetime"
          class="text__meeting-datetime my-4"
        >
          Meeting Recording on {{ meetingDateString }}
        </div> -->

        <!-- description learning outcome/addition criterion show more/show less-->
        <BaseHideContentWrapper class="mt-3" :active="activeHideContentWrapper">
          <!-- title -->
          <b v-if="post.title" class="text__post-title" :class="postTitleClasses">
            {{ post.title }}
          </b>

          <!-- description -->
          <base-text-editor-display>
            <div
              v-if="post.description"
              v-safe-html="post.description"
              class="text__post-description text-black-50 mt-3"
            />
          </base-text-editor-display>

          <!-- Competency -->
          <div
            v-if="
              post.is_submission && ultimateLearningOutcomes && criteriaUltimateLearningOutcomes
            "
          >
            <b>Competency</b>
            <p v-safe-html="ultimateLearningOutcomes.title" class="text-black-50"></p>
            <div v-for="criteria_id in post.criterion_ids" :key="`ultimate-lo__${criteria_id}`">
              <BaseLearningOutcomeAndCriterion
                v-if="criteriaUltimateLearningOutcomes[criteria_id]"
                head-title="Criteria"
                :title="criteriaUltimateLearningOutcomes[criteria_id].title"
                :description="criteriaUltimateLearningOutcomes[criteria_id].description"
                :pass-criterion="criteriaUltimateLearningOutcomes[criteria_id].pass_criterion"
                :not-yet-criterion="criteriaUltimateLearningOutcomes[criteria_id].not_yet_criterion"
                class="mt-3 container__lo"
              />
            </div>
          </div>

          <!-- Sub-learning outcome -->
          <div
            v-if="post.is_submission && subLearningOutcomes && criteriaSubLearningOutcomes"
            class="mt-4"
          >
            <div
              v-for="(criteriaSubLOGroup, key) in criteriaSubLearningOutcomesGroupById"
              :key="`sub-lo__${key}`"
            >
              <div v-if="subLearningOutcomes[key]">
                <b>Sub-learning outcome</b>
                <p v-safe-html="subLearningOutcomes[key].title" class="text-black-50"></p>
              </div>

              <div v-for="criteria in criteriaSubLOGroup" :key="`criteria-sub-lo__${criteria.id}`">
                <BaseLearningOutcomeAndCriterion
                  head-title="Criteria"
                  :title="criteria.title"
                  :learning-outcome="learningUnit.ultimate_learning_outcome"
                  :description="criteria.description"
                  :pass-criterion="criteria.pass_criterion"
                  :not-yet-criterion="criteria.not_yet_criterion"
                  class="mb-3 container__assessment-criteria"
                />
              </div>
            </div>
          </div>
        </BaseHideContentWrapper>

        <!-- image -->
        <BaseCarousel
          v-if="post.images && post.images.length > 0"
          :items="post.images"
          :options="{ slidesToScroll: 1 }"
          class="carousel__post"
        >
          <template v-slot:carousel-body="{ item, index }">
            <div class="slider-item d-flex justify-content-center carousel__post-item">
              <base-presigned-image
                :id="`item-carousel-${index}`"
                :file-id="item.id"
                style="max-width: 100% !important"
              ></base-presigned-image>

              <!-- <b-img-lazy
                :id="`item-carousel-${index}`"
                :srcset="item.url"
                :title="item.name"
                style="max-width: 100% !important"
                v-bind="{
                  blank: true,
                  blankColor: '#bbb'
                }"
              /> -->
            </div>
          </template>
        </BaseCarousel>

        <!-- video -->
        <base-video-bunny-player
          v-if="isShowVideoPlayer && post.bunny_video"
          :bunny-video="post.bunny_video"
          class="d-flex align-items-center justify-content-center mt-4 video__post"
        ></base-video-bunny-player>

        <!-- attachment -->
        <BaseFilesList
          v-if="isShowAttachment && post.attachments && post.attachments.length > 0"
          class="mt-4 attachment__post"
          :attachments="post.attachments"
        />

        <b-alert
          variant="warning"
          :show="post.meeting && !(isRecordProcessComplete && isRecordEnd)"
          class="mt-u4"
          >One moment - we're processing the recording</b-alert
        >

        <!-- meeting records -->
        <meeting-records
          v-if="showMeetingRecord && isRecordProcessComplete && isRecordEnd"
          :post="post"
          :meeting-records="post.meeting.meeting_records"
          class="mt-4"
        >
        </meeting-records>

        <!-- mark complete -->
        <div
          v-if="
            showMeetingRecord &&
            isRecordProcessComplete &&
            isRecordEnd &&
            isShowMarkComplete &&
            post.is_submission === false &&
            post.meeting
          "
        >
          <b-button
            :variant="isPostMarked ? 'outline-secondary' : 'outline-primary'"
            class="mt-4"
            @click="onClickPostMark"
          >
            <b-spinner v-if="isUpdatingPostMark" small label="updating post mark" />

            <span v-else>
              <font-awesome-icon
                v-if="isPostMarked"
                :icon="{ prefix: 'fas', iconName: 'check' }"
                size="lg"
                class="mr-2"
              />
              Mark as read
            </span>
          </b-button>

          <!-- <div
            v-if="
              isShowMarkComplete &&
                post.is_submission === false &&
                post.meeting &&
                isUpdatingPostMark
            "
            class="mt-4 mb-2 post__mark-complete-checkbox"
          >
            <b-spinner
              small
              label="updating post mark"
            />
          </div>
          <b-form-checkbox
            v-if="
              isShowMarkComplete &&
                post.is_submission === false &&
                post.meeting &&
                !isUpdatingPostMark
            "
            v-model="isPostMarked"
            class="mt-4 mb-2 post__mark-complete-checkbox"
            :class="classMarkCompleteCheckBox"
          >
            Mark Complete
          </b-form-checkbox> -->
        </div>

        <div
          v-else-if="!isOwner && isShowMarkComplete && !post.is_submission && post.meeting == null"
        >
          <b-button
            :variant="isPostMarked ? 'outline-secondary' : 'outline-primary'"
            class="mt-4"
            @click="onClickPostMark"
          >
            <b-spinner v-if="isUpdatingPostMark" small label="updating post mark" />

            <span v-else>
              <font-awesome-icon
                v-if="isPostMarked"
                :icon="{ prefix: 'fas', iconName: 'check' }"
                size="lg"
                class="mr-2"
              />
              Mark as read
            </span>
          </b-button>
        </div>

        <!-- View Conference actions -->
        <div
          v-if="post && post.meeting !== null && isOwner && isRecordProcessComplete && isRecordEnd"
          class="d-flex align-items-center mt-4"
        >
          <!-- <router-link v-if="isShowViewRecording" :to="linkVideoConferencePage">
            <b-button
              ref="button__view-recording"
              class="button__view-recording mr-3"
              variant="primary"
            >
              View recording
            </b-button>
          </router-link> -->

          <b-button
            v-if="!post.is_published"
            ref="button__publish-recording"
            class="button__publish-recording mr-3"
            variant="outline-primary"
            @click="showModalPublishPost"
          >
            Publish
          </b-button>

          <!-- <b-badge
            ref="tag__publish"
            class="tag__post"
            :class="tagPublishClasses"
            :variant="tagPublishVariant"
          >
            {{ post.is_published ? "Published" : "Unpublished" }}
          </b-badge> -->

          <base-publish-post-modal
            :ref="`modal-publish-post-${post.id}`"
            v-model="post.is_published"
            :post-id="post.id"
            :post-schedule="postSchedule"
            @saveSuccessful="isPublished"
          />
        </div>

        <!-- Check consent -->
        <div
          v-if="checkConsent && !isOwner && (!consent || consent.consent === false) && consentType"
          class="card border-none rounded-0 shadow-none bg-light p-3 d-flex flex-column align-items-center"
        >
          <div>
            {{ consentType.description }}
          </div>
          <div>
            <b-button class="mt-3" variant="primary" @click="createConsent">
              <b-spinner v-if="isFetching" />
              <div v-else>I agree</div>
            </b-button>
          </div>
        </div>

        <div v-if="checkConsent && !isOwner && consent && consent.consent === true">
          <b-alert variant="info" show>
            {{ consentType.additional_message }}
          </b-alert>

          <!-- <router-link v-if="isShowViewRecording" :to="linkVideoConferencePage">
            <b-button
              ref="button__view-recording"
              class="button__view-recording mr-3"
              variant="primary"
            >
              View recording
            </b-button>
          </router-link> -->
        </div>
      </div>

      <slot name="footer" :post="post" />
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Section from '@/models/Section';
import Post from '@/models/Post';
import LearningUnit from '@/models/LearningUnit';
import PostSchedule from '@/models/PostSchedule';
import LearningOutcome from '@/models/LearningOutcome';
import PostMark from '@/models/PostMark';
import Consent from '@/models/Consent';
import ConsentType from '@/models/ConsentType';
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { pageWidthChecker } from '@/mixins/pageWidthChecker';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import BaseFilesList from '@/components/base-components/BaseFilesList';
import BaseCarousel from '@/components/base-components/BaseCarousel';
import BaseHideContentWrapper from '@/components/base-components/BaseHideContentWrapper';
import BaseLearningOutcomeAndCriterion from './BaseLearningOutcomeAndCriterion';
import BasePublishPostModal from './BasePublishPostModal';
import BaseEllipsis from '@/components/base-components/BaseEllipsis';
import ModalDeletePost from '@/views/Post/components/ModalDeletePost';
import Criteria from '@/models/Criteria';
import { groupBy } from 'lodash';
import MeetingRecords from '@/components/Post/MeetingRecords';
import BaseTextEditorDisplay from '@/components/base-components/BaseTextEditorDisplay';
import BasePresignedImage from '@/components/base-components/BasePresignedImage.vue';
import BaseVideoBunnyPlayer from '@/components/base-components/BaseVideoBunnyPlayer.vue';

const badgesLearning = {
  text: 'Learning',
  class: 'text-second tag__post tag--learning',
  variant: 'neutral-second'
};
const badgesAssessment = {
  text: 'Assessment',
  class: 'text-primary tag__post tag--evaluate-learning-outcome',
  variant: 'neutral-primary'
};
const badgesVideoConference = {
  text: 'Video Conference',
  class: 'text-info tag__post tag--video-conference',
  variant: 'neutral-info'
};
const badgesLearningOutcome = {
  text: 'Evaluate learning outcome',
  class: 'text-primary tag__post tag--evaluate-lo',
  variant: 'neutral-primary'
};

export default {
  components: {
    FontAwesomeIcon,
    BaseVideoBunnyPlayer,
    BaseFilesList,
    BaseCarousel,
    BaseHideContentWrapper,
    BaseLearningOutcomeAndCriterion,
    BasePublishPostModal,
    BaseEllipsis,
    ModalDeletePost,
    MeetingRecords,
    BaseTextEditorDisplay,
    BasePresignedImage
  },

  mixins: [datetimeMixin, pageWidthChecker, userInfoMixin],

  props: {
    post: {
      type: Post,
      default: () => ({})
    },

    learningUnit: {
      type: LearningUnit,
      default: () => ({})
    },

    // date: {
    //   type: Date,
    //   default: () => new Date(),
    // },

    isPrivate: {
      type: Boolean,
      default: false
    },

    isShowFullContent: {
      type: Boolean,
      default: true
    },

    isShowMarkComplete: {
      type: Boolean,
      default: false
    },

    showDate: {
      type: Boolean,
      default: true
    },

    sectionId: {
      type: Number,
      default: null
    },

    section: {
      type: Section,
      default: () => ({})
    },

    isShowVideoPlayer: {
      type: Boolean,
      default: true
    },

    isShowViewRecording: {
      type: Boolean,
      default: true
    },

    isShowAttachment: {
      type: Boolean,
      default: true
    },

    isOwner: {
      type: Boolean
    },

    checkConsent: {
      type: Boolean,
      default: false
    },

    activeHideContentWrapper: {
      type: Boolean,
      default: true
    },

    postSchedule: {
      type: PostSchedule,
      default: () => ({})
    },

    disabled: {
      type: [Number, Boolean],
      default: false
    },

    editable: {
      type: Boolean
    }
  },

  data() {
    return {
      postWidth: null,
      isUpdatingPostMark: false,
      isFetching: false,
      showModalDelete: false
    };
  },

  computed: {
    hidePostVideoConference() {
      return this.post.meeting && this.post.meeting.meeting_records
        ? this.post.meeting.meeting_records.length === 0
        : false;
    },

    showMeetingRecord() {
      return this.checkConsent
        ? !this.isOwner && this.consent
        : this.post.meeting &&
            this.post.meeting.meeting_records &&
            this.post.meeting.meeting_records.length;
    },

    isRecordProcessComplete() {
      const { meeting_records = [] } = this.post?.meeting;

      const isListedMeetingRecords = meeting_records.filter(
        (meetingRecord) => meetingRecord.is_listed
      );

      return isListedMeetingRecords.length
        ? isListedMeetingRecords.every((record) => record.vimeo?.transcode_status === 'complete')
        : true;
    },

    isRecordEnd() {
      const startAt = new Date(this.post.meeting.start_time).getTime();
      const endAt = startAt + 45 * 60 * 1000;
      return Date.now() > endAt;
    },

    ultimateLearningOutcomes() {
      return LearningOutcome.query()
        .whereId(this.learningUnit.ultimate_learning_outcome_id)
        .first();
    },

    subLearningOutcomes() {
      let subLearningOutcomes = {};
      if (this.ultimateLearningOutcomes) {
        LearningOutcome.query()
          .whereId(this.ultimateLearningOutcomes.sub_learning_outcome_ids)
          .get()
          .map(
            (subLearningOutcome) =>
              (subLearningOutcomes[subLearningOutcome.id] = subLearningOutcome)
          );

        return Object.keys(subLearningOutcomes).length === 0 ? null : subLearningOutcomes;
      }
      return null;
    },

    criteriaUltimateLearningOutcomes() {
      let criterions = {},
        criterion_ultimate_lo = {};

      Criteria.query()
        .whereId(this.ultimateLearningOutcomes.criterion_ids)
        .get()
        .map((criterion) => (criterions[criterion.id] = criterion));

      this.post.criterion_ids.forEach((id) => {
        if (criterions[id]) {
          criterion_ultimate_lo[id] = criterions[id];
        }
      });

      return Object.keys(criterion_ultimate_lo).length === 0 ? null : criterion_ultimate_lo;
    },

    criteriaSubLearningOutcomes() {
      let criterion_ids = [],
        criterions = {},
        criteria_sub_lo = {};

      LearningOutcome.query()
        .whereId(this.ultimateLearningOutcomes.sub_learning_outcome_ids)
        .get()
        .map((subLearningOutcome) => criterion_ids.push(...subLearningOutcome.criterion_ids));

      Criteria.query()
        .whereId(criterion_ids)
        .with('learning_outcome')
        .get()
        .map((criterion) => (criterions[criterion.id] = criterion));

      this.post.criterion_ids.forEach((criterion_id) => {
        if (criterions[criterion_id]) {
          criteria_sub_lo[criterion_id] = criterions[criterion_id];
        }
      });

      return Object.keys(criteria_sub_lo).length === 0 ? null : criteria_sub_lo;
    },

    criteriaSubLearningOutcomesGroupById() {
      return this.criteriaSubLearningOutcomes
        ? groupBy(this.criteriaSubLearningOutcomes, 'learning_outcome_id')
        : null;
    },

    scheduleDate() {
      return this.postSchedule && this.postSchedule.published_at
        ? new Date(this.postSchedule.published_at)
        : '';
    },

    meetingDate() {
      return this.post && this.post.meeting && this.post.meeting.start_time
        ? new Date(this.post.meeting.start_time)
        : '';
    },

    badges() {
      const badges = [];

      if (this.post.is_submission) {
        badges.push(badgesAssessment);

        if (this.post.criterion_ids && this.post.criterion_ids.length) {
          badges.push(badgesLearningOutcome);
        }
      } else if (this.post.meeting !== null) {
        badges.push(badgesVideoConference);
      } else {
        badges.push(badgesLearning);
      }

      return badges;
    },

    scheduleDateString() {
      return `${this.formatedDatetime(this.scheduleDate, 'MMM D, YYYY hh:mma ')} ${
        this.cityTimezone
      } time`;
    },

    meetingDateString() {
      return `${this.formatedDatetime(this.meetingDate, 'MMM D, YYYY hh:mma ')} ${
        this.cityTimezone
      } time`;
    },

    videoWidth() {
      return this.postWidth ? this.postWidth * 0.925 : this.windowWidth <= 650 ? 500 : 646;
    },

    videoHeight() {
      return this.windowWidth <= 650 ? 250 : 343;
    },

    isPostMarked() {
      return this.post && this.user ? PostMark.isUserMarkedPost(this.post.id, this.user.id) : false;
    },

    // isPostMarked: {
    //   get() {
    //     return this.post && this.user
    //       ? PostMark.isUserMarkedPost(this.post.id, this.user.id)
    //       : false;
    //   },

    //   async set(v) {
    //     this.isUpdatingPostMark = true;

    //     await PostMark.api().createOrUpdate(this.post.id, this.user.id, v);

    //     this.isUpdatingPostMark = false;
    //   },
    // },

    classMarkCompleteCheckBox() {
      return {
        checked: this.isPostMarked
      };
    },

    tagPublishClasses() {
      return {
        [this.post.is_published ? 'tag--published' : 'tag--unpublished']: true,
        [this.post.is_published ? 'text-success' : 'text-danger']: true
      };
    },

    postTitleClasses() {
      return {
        'text__meeting-datetime': this.post && this.post.meeting
      };
    },

    tagPublishVariant() {
      return this.post.is_published ? 'neutral-success' : 'neutral-danger';
    },

    linkVideoConferencePage() {
      return {
        name: `learning-unit/video-conference/${this.isOwner ? 'teacher' : 'learner'}`,
        params: {
          learningUnitId: this.post.learning_unit_id,
          postId: this.post.id,
          sectionId: this.section.id
        },
        query: {
          previousPath: this.$route.fullPath
        }
      };
    },

    consent() {
      return this.post && this.user && this.section
        ? Consent.query()
            .where('post_id', this.post.id)
            .where('user_id', this.user.id)
            .where('section_id', this.section.id)
            .first()
        : null;
    },

    consentType() {
      return ConsentType.query().first();
    }
  },

  mounted() {
    this.validateConsent();
  },

  methods: {
    async createConsent() {
      try {
        this.isFetching = true;

        const body = {
          user: this.user.id,
          uid: this.user.uid,
          post: this.post.id,
          section: this.section.id,
          consent: true,
          consent_type: 1
        };

        await Consent.api().create(body);

        await this.validateConsent();

        this.isFetching = false;

        this.$toast.open({
          message: 'You have agreed not to share this recording.',
          type: 'success',
          pauseOnHover: true
        });
      } catch (error) {
        this.$toast.open({
          message: 'Your consent failed to update, please try again.',
          type: 'error',
          duration: 5000
        });
      }
    },

    showModalPublishPost() {
      const ref = this.$refs[`modal-publish-post-${this.post.id}`];

      if (ref) {
        ref.show();
      }
    },

    async validateConsent() {
      if (this.checkConsent) {
        try {
          ConsentType.api().fetch({
            name: 'Sharing Recording Agreement'
          });

          await Consent.api().fetch({
            user: this.user.id,
            post: this.post.id,
            section: this.section.id
          });
        } catch (error) {
          console.log(error);
          this.$toast.open({
            message: 'Fail to fetch data',
            type: 'error',
            duration: 5000
          });
        }
      }
    },

    goToEditPost(postId) {
      if (this.post.meeting !== null) {
        this.$router.push({
          name: 'design-planning/post/edit-video-conference',
          params: {
            learningUnitId: this.learningUnit.id,
            sectionId: this.sectionId,
            postId
          },
          query: {
            previousPath: this.$route.fullPath
          }
        });
      } else {
        this.$router.push({
          name: 'design-planning/post/edit',
          params: {
            learningUnitId: this.learningUnit.id,
            sectionId: this.sectionId,
            postId
          },
          query: {
            previousPath: this.$route.fullPath,
            view: this.$route.query.view || 'feed'
          }
        });
      }
    },

    async onClickPostMark() {
      this.isUpdatingPostMark = true;

      await PostMark.api().createOrUpdate(this.post.id, this.user.id, !this.isPostMarked);

      this.$_getSiteControlMixin_updateParams({
        mc_learning_progress: this.learningUnit.getLearningProgress(this.user.id)
      });

      this.isUpdatingPostMark = false;
    },

    // showModal(modalType) {
    //   console.log(1234, this.$refs);

    //   if (this.$refs[modalType]) {
    //     this.$refs[modalType].show(modalType);
    //   }
    // },

    emitDeletePost(postId) {
      this.$emit('post:deleted', postId);
    },

    isPublished() {
      this.$emit('post:published', true);
    }
  }
};
</script>

<style lang="scss" scoped>
.btn-outline-secondary {
  color: #75767e;
  border-color: #75767e;

  &:hover {
    color: #fff;
    background-color: #6c757d;
    border-color: #6c757d;
  }
}

.card__post {
  width: 100%;

  .post__date {
    color: rgba(7, 9, 25, 0.5);
  }

  .carousel__post-item {
    width: 100%;
    max-width: 680x;
    height: 300px;
  }
}
</style>
